import { useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Center,
  Group,
  Paper,
  Skeleton,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { IconArrowDown, IconArrowUp, IconArrowsSort } from '@tabler/icons-react'

import type { ByApiKeyEntry } from '@/api/whatsapiStats'
import { useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useByApiKey, isNoApiKeys } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

type SortKey = 'name' | 'sent' | 'failed' | 'avg_latency_ms' | 'last_used_at'
type SortDir = 'asc' | 'desc'

function formatLastUsed(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pt-BR')
  } catch {
    return value
  }
}

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '0'
  return n.toLocaleString('pt-BR')
}

function compareValues(a: ByApiKeyEntry, b: ByApiKeyEntry, key: SortKey, dir: SortDir): number {
  const sign = dir === 'asc' ? 1 : -1
  const av = a[key]
  const bv = b[key]
  if (av === null && bv === null) return 0
  if (av === null) return 1 * sign
  if (bv === null) return -1 * sign
  if (typeof av === 'number' && typeof bv === 'number') {
    return (av - bv) * sign
  }
  return String(av).localeCompare(String(bv), 'pt-BR') * sign
}

interface SortableHeaderProps {
  label: string
  active: boolean
  dir: SortDir
  onClick: () => void
  align?: 'left' | 'right'
}

function SortableHeader({ label, active, dir, onClick, align = 'left' }: SortableHeaderProps) {
  const Icon = !active ? IconArrowsSort : dir === 'asc' ? IconArrowUp : IconArrowDown
  return (
    <UnstyledButton onClick={onClick} aria-label={`Ordenar por ${label}`}>
      <Group gap={4} justify={align === 'right' ? 'flex-end' : 'flex-start'}>
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Icon size={14} />
      </Group>
    </UnstyledButton>
  )
}

interface ApiKeyTableProps {
  escopo?: EscopoWhatsApi
}

export function ApiKeyTable({ escopo }: ApiKeyTableProps = {}) {
  const { period } = useWhatsApiPeriod()
  const query = useByApiKey(period, { escopo })

  const [sortKey, setSortKey] = useState<SortKey>('sent')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const rows = useMemo(() => {
    const raw = query.data
    const data = raw && !isNoApiKeys(raw) ? (raw.keys ?? []) : []
    return [...data].sort((a, b) => compareValues(a, b, sortKey, sortDir))
  }, [query.data, sortKey, sortDir])

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Title order={5} mb="sm">
        API Keys
      </Title>

      {query.isLoading && <Skeleton height={160} radius="md" />}

      {!query.isLoading && rows.length === 0 && (
        <Center py="lg" data-testid="api-key-empty">
          <Text c="dimmed" size="sm">
            Nenhum dado disponível para este período
          </Text>
        </Center>
      )}

      {!query.isLoading && rows.length > 0 && (
        <Box style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <SortableHeader
                    label="Nome"
                    active={sortKey === 'name'}
                    dir={sortDir}
                    onClick={() => handleSort('name')}
                  />
                </Table.Th>
                <Table.Th>Ativo</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  <SortableHeader
                    label="Enviadas"
                    active={sortKey === 'sent'}
                    dir={sortDir}
                    onClick={() => handleSort('sent')}
                    align="right"
                  />
                </Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  <SortableHeader
                    label="Falhadas"
                    active={sortKey === 'failed'}
                    dir={sortDir}
                    onClick={() => handleSort('failed')}
                    align="right"
                  />
                </Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  <SortableHeader
                    label="Latência média"
                    active={sortKey === 'avg_latency_ms'}
                    dir={sortDir}
                    onClick={() => handleSort('avg_latency_ms')}
                    align="right"
                  />
                </Table.Th>
                <Table.Th>
                  <SortableHeader
                    label="Último uso"
                    active={sortKey === 'last_used_at'}
                    dir={sortDir}
                    onClick={() => handleSort('last_used_at')}
                  />
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id ?? row.name}>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>
                    <Badge
                      color={row.is_active ? 'green' : 'gray'}
                      variant="light"
                      size="sm"
                    >
                      {row.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatNumber(row.sent)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatNumber(row.failed)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {`${Math.round(row.avg_latency_ms ?? 0).toLocaleString('pt-BR')} ms`}
                  </Table.Td>
                  <Table.Td>{formatLastUsed(row.last_used_at)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Paper>
  )
}
